<template>
  <div class="fak-page">
    <div class="fak-hero-header">
      <section class="fak-hero">
        <AtsHeroBackground class="fak-hero__bg" :src="assets.heroBg" fallback-color="#f5f5f5" />
        <div class="fak-hero__mask" aria-hidden="true" />
        <div class="fak-hero__inner">
          <div class="fak-hero__content">
            <h1 class="fak-hero__title">急救包产品</h1>
            <p class="fak-hero__desc">
              提供各类急救包、防汛包等急救物品、设备的批量定制化（含 logo）采购。场景覆盖：家用、办公楼宇、工厂厂房、医疗、消防、防汛、户外、车载等。性价比高，品质保障！
            </p>
          </div>
        </div>
      </section>
    </div>

    <div class="fak-products">
      <section
        v-for="(row, index) in productRows"
        :key="row.title"
        class="fak-product"
        :class="{
          'fak-product--reverse': row.reverse,
          'fak-product--portable': index === 0
        }"
        :aria-label="row.title"
      >
        <div
          class="fak-product__media fak-product__pane--gray"
          :class="{ 'fak-product__media--full': row.hideProductImg }"
          :style="getProductMediaPaneStyle(row)"
        >
          <img
            v-if="!row.hideProductImg"
            class="fak-product__img"
            :src="row.image"
            :alt="row.title"
            loading="lazy"
            decoding="async"
          >
        </div>
        <div class="fak-product__body fak-product__pane--white">
          <div class="fak-product__content">
            <span class="fak-product__tag">{{ row.tag }}</span>
            <h2 class="fak-product__title">{{ row.title }}</h2>
            <p class="fak-product__desc">{{ row.desc }}</p>
            <button type="button" class="fak-product__btn" :style="learnMoreBtnStyle" @click.prevent="onProductDetailClick(row)">
              <span class="fak-product__btn-text">查看详情</span>
              <img
                class="fak-product__btn-arrow"
                :src="assets.iconArrowSmall"
                alt=""
                role="presentation"
                width="12"
                height="12"
                decoding="async"
              >
            </button>
          </div>
        </div>
      </section>
    </div>

    <AtsSiteFooter />

    <!-- 医用应急包详情弹框 -->
    <transition name="fak-modal">
      <div
        v-if="medicalDetailVisible"
        class="fak-modal"
        role="presentation"
        @click.self="closeMedicalDetail"
      >
        <div
          class="fak-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fak-medical-detail-title"
        >
          <header class="fak-modal__head">
            <h2 id="fak-medical-detail-title" class="fak-modal__title">医用应急包详情</h2>
            <button
              type="button"
              class="fak-modal__close"
              aria-label="关闭"
              @click="closeMedicalDetail"
            >
              <span aria-hidden="true">×</span>
            </button>
          </header>
          <div class="fak-modal__body">
            <table class="fak-detail-table">
              <thead>
                <tr>
                  <th scope="col">序号</th>
                  <th scope="col">产品名称</th>
                  <th scope="col">规格</th>
                  <th scope="col">单位</th>
                  <th scope="col">数量</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in medicalKitItems" :key="item.name">
                  <td>{{ index + 1 }}</td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.spec }}</td>
                  <td>{{ item.unit }}</td>
                  <td>{{ item.qty }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AtsSiteFooter from '@/components/ats/AtsSiteFooter.vue'
import AtsHeroBackground from '@/components/ats/AtsHeroBackground.vue'

/**
 * 急救包产品专题页：素材来自 `src/assets/firstaidkit`（英文命名）
 */
export default {
  name: 'FirstAidKitPage',
  components: {
    AtsSiteFooter,
    AtsHeroBackground
  },
  data () {
    return {
      medicalDetailVisible: false,
      /** @type {number} 弹框打开前页面滚动位置 */
      bodyScrollTop: 0,
      /** @type {HTMLElement|null} 滚动锁定目标节点 */
      scrollLockTarget: null,
      /** @type {Array<{ name: string, spec: string, unit: string, qty: string }>} */
      medicalKitItems: [
        { name: '医用口罩', spec: '单片装', unit: '支', qty: '2' },
        { name: '医用检查手套', spec: '单片装', unit: '支', qty: '2' },
        { name: '酒精棉片', spec: '单片装', unit: '支', qty: '2' },
        { name: '创口贴', spec: '单片装', unit: '支', qty: '2' },
        { name: '75%酒精喷雾', spec: '单片装', unit: '支', qty: '2' },
        { name: '洗手凝胶', spec: '单片装', unit: '支', qty: '2' },
        { name: '碘伏消毒液（棉棒装）', spec: '单片装', unit: '支', qty: '2' },
        { name: '退热贴', spec: '单片装', unit: '支', qty: '2' },
        { name: '体温计', spec: '单片装', unit: '支', qty: '2' },
        { name: '药盒', spec: '单片装', unit: '支', qty: '2' },
        { name: '产品清单', spec: '单片装', unit: '支', qty: '2' },
        { name: '急救手册', spec: '单片装', unit: '支', qty: '2' },
        { name: '合格证', spec: '单片装', unit: '支', qty: '2' },
        { name: '外包', spec: '单片装', unit: '支', qty: '2' }
      ],
      assets: {
        heroBg: require('@/assets/firstaidkit/hero_bg_first_aid.png'),
        heroBgAlt: require('@/assets/firstaidkit/hero_bg_first_aid_alt.png'),
        productPortable: require('@/assets/firstaidkit/product_portable_first_aid_kit.png'),
        productMedical: require('@/assets/firstaidkit/product_medical_emergency_kit.png'),
        productHousehold: require('@/assets/firstaidkit/product_household_first_aid_kit.png'),
        household: require('@/assets/firstaidkit/household.png'),
        inVehicle: require('@/assets/firstaidkit/in-vehicle.png'),
        productCar: require('@/assets/firstaidkit/product_car_emergency_kit.png'),
        productEmergencyBox: require('@/assets/firstaidkit/product_emergency_first_aid_box.png'),
        emergency: require('@/assets/firstaidkit/emergency.png'),
        uiBtnLearnMore: require('@/assets/firstaidkit/ui_btn_learn_more.png'),
        iconArrowSmall: require('@/assets/firstaidkit/icon_arrow_right_small.png')
      }
    }
  },
  computed: {
    /**
     * 产品行（左右交替：图左文右 / 文左图右）
     * @returns {Array<{ tag: string, title: string, desc: string, image: string, reverse: boolean, mediaBg?: string, hideProductImg?: boolean, detailModal?: string }>}
     */
    productRows () {
      const a = this.assets
      return [
        {
          tag: '急救包',
          title: '便携性急救包',
          desc: '使用场景：员工福利，家庭防护，户外探险、运动损伤、交通事故、 自然灾害及突发疾病等场景。',
          image: a.productPortable,
          mediaBg: a.heroBgAlt,
          hideProductImg: true,
          reverse: false
        },
        {
          tag: '医用系列',
          title: '医用应急包',
          desc: '使用场景：日常与家庭场景、户外与运动场景。',
          image: a.productMedical,
          detailModal: 'medical',
          reverse: true
        },
        {
          tag: '家用系列',
          title: '家用急救包',
          desc: '使用场景：家庭防护场景、户外与运动场景、公共与特定风险场景、 特殊关怀场景。',
          image: a.productHousehold,
          mediaBg: a.household,
          hideProductImg: true,
          reverse: false
        },
        {
          tag: '车载系列',
          title: '车载应急包',
          desc: '使用场景：交通事故、自驾旅行、赛车竞速。',
          image: a.productCar,
          mediaBg: a.inVehicle,
          hideProductImg: true,
          reverse: true
        },
        {
          tag: '专业系列',
          title: '应急急救箱',
          desc: '使用场景：企业置放、工厂维护、家庭应急、运动防护，处理轻微创伤 突发不适或扭伤擦伤。',
          image: a.productEmergencyBox,
          mediaBg: a.emergency,
          hideProductImg: true,
          reverse: false
        }
      ]
    },
    /**
     * 了解更多按钮背景
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string }}
     */
    learnMoreBtnStyle () {
      return {
        backgroundImage: `url(${this.assets.uiBtnLearnMore})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    }
  },
  methods: {
    /**
     * 产品图灰底区域背景
     * @param {{ mediaBg?: string }} row
     * @returns {{ backgroundImage: string, backgroundSize: string, backgroundRepeat: string, backgroundPosition: string }}
     */
    getProductMediaPaneStyle (row) {
      const bg = row.mediaBg || this.assets.heroBgAlt
      return {
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      }
    },
    /**
     * 获取滚动锁定容器（锁定 ATS 壳层，避免 body height:100% 导致背景变白）
     * @returns {HTMLElement|null}
     */
    getScrollLockTarget () {
      return document.querySelector('.ats-app-shell') || document.body
    },
    /**
     * 锁定页面滚动，保持当前视口内容不变
     * @returns {void}
     */
    lockBodyScroll () {
      this.bodyScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0
      const target = this.getScrollLockTarget()
      if (!target) return

      this.scrollLockTarget = target
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

      target.style.position = 'fixed'
      target.style.top = `-${this.bodyScrollTop}px`
      target.style.left = '0'
      target.style.right = '0'
      target.style.width = '100%'
      target.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        target.style.paddingRight = `${scrollbarWidth}px`
      }
      document.documentElement.style.overflow = 'hidden'
    },
    /**
     * 恢复页面滚动位置
     * @returns {void}
     */
    unlockBodyScroll () {
      const scrollTop = this.bodyScrollTop
      const target = this.scrollLockTarget

      if (target) {
        target.style.position = ''
        target.style.top = ''
        target.style.left = ''
        target.style.right = ''
        target.style.width = ''
        target.style.overflow = ''
        target.style.paddingRight = ''
        this.scrollLockTarget = null
      }
      document.documentElement.style.overflow = ''
      window.scrollTo(0, scrollTop)
    },
    /**
     * 查看详情按钮
     * @param {{ detailModal?: string }} row
     * @returns {void}
     */
    onProductDetailClick (row) {
      if (row.detailModal === 'medical') {
        this.lockBodyScroll()
        this.medicalDetailVisible = true
      }
    },
    /**
     * 关闭医用应急包详情弹框
     * @returns {void}
     */
    closeMedicalDetail () {
      this.medicalDetailVisible = false
      this.unlockBodyScroll()
    }
  },
  deactivated () {
    if (this.medicalDetailVisible) {
      this.medicalDetailVisible = false
      this.unlockBodyScroll()
    }
  },
  beforeDestroy () {
    if (this.medicalDetailVisible) {
      this.unlockBodyScroll()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../components/ats/mixins';
$green: #256a30;
$text-dark: #1a2226;
$gray-bg: #f4f4f4;

.fak-page {
  min-height: 100vh;
  background: #F4F4F4;
  color: $text-dark;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.fak-hero-header {
  position: relative;
  height: 871px;
  min-height: 871px;
  max-height: 871px;
}

.fak-hero {
  position: relative;
  height: 100%;
  min-height: 871px;
  overflow: hidden;
  box-sizing: border-box;
}

.fak-hero__bg {
  background-size: cover;
  background-position: center center;
}

.fak-hero__mask {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.18) 100%
  );
  pointer-events: none;
}

.fak-hero__inner {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 36px;
  box-sizing: border-box;
}

.fak-hero__content {
  width: 100%;
  max-width: 982px;
  margin: 0 auto;
  color: #ffffff;
  text-align: center;
}

.fak-hero__title {
  margin: 0 0 60px;
  font-size: 60px;
  font-weight: 800;
  line-height: 1.22;
  color: #ffffff;
  letter-spacing: 0.08em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5), 0 4px 28px rgba(0, 0, 0, 0.4);
}

.fak-hero__desc {
  margin: 0 auto;
  font-size: 24px;
  font-weight: 400;
  line-height: 1.75;
  color: #ffffff;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45), 0 2px 16px rgba(0, 0, 0, 0.35);
}

/* 产品列表容器 */
.fak-products {
  width: 80%;
  margin: 120px auto 0;
  box-sizing: border-box;
}

/* 产品行：左右各半，灰白底交替 */
.fak-product {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 520px;
  margin-top: 60px;
}

.fak-product:last-child {
  margin-bottom: 120px;
}

.fak-product--portable .fak-product__pane--gray,
.fak-product__media--full {
  padding: 0;
}

.fak-product--reverse .fak-product__media {
  order: 2;
}

.fak-product--reverse .fak-product__body {
  order: 1;
}

.fak-product__pane--gray {
  overflow: hidden;
}

.fak-product__pane--white {
  background: #ffffff;
}

.fak-product__media {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 48px 40px;
  box-sizing: border-box;
}

.fak-product__img {
  display: block;
  max-width: 100%;
  max-height: 420px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.fak-product__body {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 56px 48px 56px 64px;
  box-sizing: border-box;
}

.fak-product__content {
  max-width: 520px;
  width: 100%;
}

.fak-product__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 90px;
  height: 40px;
  margin-bottom: 32px;
  padding: 8px 18px;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
  color: #999999;
  white-space: nowrap;
  background: #ffffff;
  border: 1px solid #d8d8d8;
  border-radius: 20px;
}

.fak-product__title {
  margin: 0 0 28px;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: $text-dark;
}

.fak-product__desc {
  margin: 0 0 48px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.8;
  color: #666666;
}

.fak-product__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 180px;
  height: 60px;
  padding: 0;
  border: none;
  border-radius: 0;
  cursor: pointer;
  background-color: transparent;
  box-sizing: border-box;
  @include ats-hover-pill(1.06);
}

.fak-product__btn-text {
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  color: #ffffff;
}

.fak-product__btn-arrow {
  display: block;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  object-fit: contain;
}

.fak-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
}

.fak-modal-enter-active,
.fak-modal-leave-active {
  transition: opacity 0.18s ease;
}

.fak-modal-enter-active .fak-modal__panel,
.fak-modal-leave-active .fak-modal__panel {
  transition: opacity 0.2s cubic-bezier(0.22, 1, 0.36, 1), transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.fak-modal-enter,
.fak-modal-leave-to {
  opacity: 0;
}

.fak-modal-enter .fak-modal__panel,
.fak-modal-leave-to .fak-modal__panel {
  opacity: 0;
  transform: scale(0.94) translateY(16px);
}

@media (prefers-reduced-motion: reduce) {
  .fak-modal-enter-active,
  .fak-modal-leave-active,
  .fak-modal-enter-active .fak-modal__panel,
  .fak-modal-leave-active .fak-modal__panel {
    transition: opacity 0.12s ease;
  }

  .fak-modal-enter .fak-modal__panel,
  .fak-modal-leave-to .fak-modal__panel {
    transform: none;
  }
}

.fak-modal__panel {
  width: 100%;
  max-width: 760px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  transform-origin: center center;
  will-change: transform, opacity;
}

.fak-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 28px 32px 20px;
  box-sizing: border-box;
}

.fak-modal__title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  color: #333333;
}

.fak-modal__close {
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #c8c8c8;
  color: #ffffff;
  cursor: pointer;
  @include ats-hover-icon;
}

.fak-modal__close span {
  display: block;
  font-family: Arial, 'Helvetica Neue', sans-serif;
  font-size: 24px;
  font-weight: 400;
  line-height: 1;
  text-align: center;
}

.fak-modal__body {
  padding: 0 32px 32px;
  box-sizing: border-box;
}

.fak-detail-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 16px;
  color: #666666;
}

.fak-detail-table th,
.fak-detail-table td {
  padding: 14px 12px;
  text-align: center;
  border: 1px dotted #d8d8d8;
  word-break: break-word;
}

.fak-detail-table thead th {
  background: #e9f4ed;
  color: #4d7d66;
  font-size: 16px;
  font-weight: 600;
}

.fak-detail-table tbody td {
  background: #ffffff;
  font-weight: 400;
}

.fak-detail-table th:nth-child(1),
.fak-detail-table td:nth-child(1) {
  width: 12%;
}

.fak-detail-table th:nth-child(2),
.fak-detail-table td:nth-child(2) {
  width: 36%;
}

.fak-detail-table th:nth-child(3),
.fak-detail-table td:nth-child(3),
.fak-detail-table th:nth-child(4),
.fak-detail-table td:nth-child(4),
.fak-detail-table th:nth-child(5),
.fak-detail-table td:nth-child(5) {
  width: 17.33%;
}

@media (max-width: 960px) {
  .fak-hero-header {
    height: auto;
    min-height: 480px;
    max-height: none;
  }
  .fak-hero {
    height: auto;
    min-height: 480px;
  }
  .fak-hero__title {
    font-size: 36px;
  }
  .fak-hero__desc {
    font-size: 18px;
  }
  .fak-product {
    grid-template-columns: 1fr;
    min-height: 0;
  }
  .fak-product--reverse .fak-product__media,
  .fak-product--reverse .fak-product__body {
    order: unset;
  }
  .fak-product__media {
    min-height: 320px;
    padding: 32px 24px;
  }
  .fak-product__body {
    padding: 40px 24px 48px;
  }
  .fak-product__title {
    font-size: 28px;
  }
  .fak-modal__head {
    padding: 20px 16px 16px;
  }
  .fak-modal__body {
    padding: 0 16px 20px;
    overflow-x: auto;
  }
  .fak-modal__title {
    font-size: 22px;
  }
  .fak-detail-table {
    min-width: 560px;
    font-size: 14px;
  }
  .fak-detail-table th,
  .fak-detail-table td {
    padding: 10px 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/firstAidKit/firstAidKit.vue